import { SessionStorageItemIds } from '../../Enums';
import { IsEmpty } from '../../Helpers';

export class ApiRequestError extends Error {
    readonly status: number;
    readonly retryAfterMs?: number;
    readonly code?: string;
    readonly requestId?: string;

    constructor(message: string, status: number, retryAfterMs?: number, code?: string, requestId?: string) {
        super(message);
        this.name = 'ApiRequestError';
        this.status = status;
        this.retryAfterMs = retryAfterMs;
        this.code = code;
        this.requestId = requestId;
        Object.setPrototypeOf(this, ApiRequestError.prototype);
    }
}

const parseRetryAfterMs = (value: string | null): number | undefined => {
    if (!value)
        return undefined;

    const seconds = Number(value);
    if (Number.isFinite(seconds) && seconds >= 0)
        return seconds * 1000;

    const retryAt = Date.parse(value);
    if (!Number.isNaN(retryAt))
        return Math.max(0, retryAt - Date.now());

    return undefined;
};

class ApiBase {
    private processResponse: (response: Response) => Promise<any>;
    constructor() {
        this.processResponse = async (response: Response) => {

            if (!response.ok) {
                const body = await response.text();
                let code: string | undefined;
                let requestId: string | undefined;
                let message = body;
                try {
                    const errorResponse = JSON.parse(body);
                    code = errorResponse.code || errorResponse.Code;
                    requestId = errorResponse.requestId || errorResponse.RequestId;
                    message = errorResponse.detail || errorResponse.Detail || errorResponse.message || errorResponse.Message || 'Unknown error occurred';
                    if (errorResponse.errors !== undefined) {
                        const firstKey = Object.keys(errorResponse.errors)[0];
                        message = `${firstKey}: ${errorResponse.errors[firstKey][0]}`;
                    }
                    else if (requestId !== undefined && code === undefined) {
                        message = `<body><p>${message}</p><p>RequestId: ${requestId}</p></body>`;
                    }
                } catch {
                    // Plain-text responses retain their existing user-facing message.
                }
                throw new ApiRequestError(
                    message,
                    response.status,
                    parseRetryAfterMs(response.headers.get('Retry-After')),
                    code,
                    requestId);
            }
            const contentType = response.headers.get("content-type");

            if (contentType && contentType.indexOf("application/json") !== -1) {
                return await response.json();
            } else {
                return;
            }
        };
    }

    private async getHeaders()
    {
        const ret: HeadersInit = [
            ['Content-Type', 'application/json'],            
        ];

        const accessToken = sessionStorage.getItem((SessionStorageItemIds.AccessToken))
        if(!IsEmpty(accessToken))
        {
            ret.push(['Authorization', `bearer ${accessToken}`]);
        }
        return ret;
    }

    protected async GetAsync(relativePathAndQuery: string, signal?: AbortSignal) {
        try {
            let response = await fetch(`${sessionStorage.API_BASE_PATH}/${relativePathAndQuery}`, {
                method: 'get',
                mode: 'cors',
                // Authenticated API reads are authoritative application state. A browser may
                // otherwise reuse an earlier successful response across an onboarding token
                // handoff, presenting a pre-organization workspace after provisioning.
                cache: 'no-store',
                headers: await this.getHeaders(),
                signal: signal
            });

            return await this.processResponse(response);
        }
        catch (error) {
            if ((error as any)?.name === 'AbortError')
                throw error;
            if (error instanceof ApiRequestError)
                throw error;
            throw new Error(`${error}`);
        }
    }

    /** Used only by public, possession-gated bootstrap endpoints. */
    protected async GetPublicAsync(relativePathAndQuery: string, signal?: AbortSignal) {
        try {
            const response = await fetch(`${sessionStorage.API_BASE_PATH}/${relativePathAndQuery}`, {
                method: 'get',
                mode: 'cors',
                cache: 'no-store',
                headers: [['Content-Type', 'application/json']],
                referrerPolicy: 'strict-origin-when-cross-origin',
                signal,
            });
            return await this.processResponse(response);
        }
        catch (error) {
            if ((error as any)?.name === 'AbortError')
                throw error;
            if (error instanceof ApiRequestError)
                throw error;
            throw new Error(`${error}`);
        }
    }

    protected async PostAsync(
        relativePathAndQuery: string,
        bodyObj = null,
        signal?: AbortSignal,
        contentType: string = 'application/json',
        additionalHeaders: [string, string][] = [],
    ) {
        try {
            const requestHeaders = new Headers(await this.getHeaders());
            requestHeaders.set('Content-Type', contentType);
            additionalHeaders.forEach(([key, value]) => requestHeaders.append(key, value));

            let response = await fetch(`${sessionStorage.API_BASE_PATH}/${relativePathAndQuery}`, {
                method: 'post',
                headers: requestHeaders,
                body: contentType === 'application/json' ? (bodyObj ? JSON.stringify(bodyObj) : bodyObj) : bodyObj,
                signal: signal
            });
            return await this.processResponse(response);
        }
        catch (error) {
            if ((error as any)?.name === 'AbortError')
                throw error;
            if (error instanceof ApiRequestError)
                throw error;
            throw new Error(`${error}`);
        }
    }

    /** Used only by public, narrowly scoped identity handshakes. */
    protected async PostPublicAsync(
        relativePathAndQuery: string,
        bodyObj = null,
        signal?: AbortSignal,
        additionalHeaders: [string, string][] = [],
        referrerPolicy: ReferrerPolicy = 'strict-origin-when-cross-origin',
    ) {
        try {
            const response = await fetch(`${sessionStorage.API_BASE_PATH}/${relativePathAndQuery}`, {
                method: 'post',
                headers: [['Content-Type', 'application/json'], ...additionalHeaders],
                body: bodyObj ? JSON.stringify(bodyObj) : null,
                referrerPolicy,
                signal,
            });
            return await this.processResponse(response);
        }
        catch (error) {
            if ((error as any)?.name === 'AbortError')
                throw error;
            if (error instanceof ApiRequestError)
                throw error;
            throw new Error(`${error}`);
        }
    }

    protected async PostPublicWithResponseHeaderAsync(
        relativePathAndQuery: string,
        bodyObj: any,
        responseHeaderName: string,
        signal?: AbortSignal,
    ) {
        try {
            const response = await fetch(`${sessionStorage.API_BASE_PATH}/${relativePathAndQuery}`, {
                method: 'post',
                headers: [['Content-Type', 'application/json']],
                body: JSON.stringify(bodyObj),
                referrerPolicy: 'strict-origin-when-cross-origin',
                signal,
            });
            const data = await this.processResponse(response);
            return { Data: data, Header: response.headers.get(responseHeaderName) };
        }
        catch (error) {
            if ((error as any)?.name === 'AbortError')
                throw error;
            if (error instanceof ApiRequestError)
                throw error;
            throw new Error(`${error}`);
        }
    }

    protected async UploadAsync(relativePathAndQuery: string, formData: FormData, signal?: AbortSignal) {
        try {
            let response = await fetch(`${sessionStorage.API_BASE_PATH}/${relativePathAndQuery}`, {
                method: 'post',
                body: formData,
                signal: signal
            });
            return await this.processResponse(response);
        }
        catch (error) {
            if ((error as any)?.name === 'AbortError')
                throw error;
            if (error instanceof ApiRequestError)
                throw error;
            throw new Error(`${error}`);
        }
    }

    protected async PutAsync(relativePathAndQuery: string, bodyObj = null, signal?: AbortSignal, contentType = 'application/json') {
        try {
            let response = await fetch(`${sessionStorage.API_BASE_PATH}/${relativePathAndQuery}`, {
                method: 'put',
                mode: 'cors',
                headers: await this.getHeaders(),
                body: contentType === 'application/json' && bodyObj ? JSON.stringify(bodyObj) : bodyObj,
                signal: signal
            });

            return await this.processResponse(response);
        }
        catch (error) {
            if ((error as any)?.name === 'AbortError')
                throw error;
            if (error instanceof ApiRequestError)
                throw error;
            throw new Error(`${error}`);
        }
    }

    protected async DeleteAsync(relativePathAndQuery: string, signal?: AbortSignal) {
        try {
            let response = await fetch(`${sessionStorage.API_BASE_PATH}/${relativePathAndQuery}`, {
                method: 'delete',
                mode: 'cors',
                headers: await this.getHeaders(),
                signal: signal
            });
            return await this.processResponse(response);
        }
        catch (error) {
            if ((error as any)?.name === 'AbortError')
                throw error;
            if (error instanceof ApiRequestError)
                throw error;
            throw new Error(`${error}`);
        }
    }
}

export default ApiBase;
